import {
  lineString as turfLineString,
  polygon as turfPolygon,
  lineIntersect,
  nearestPointOnLine,
  point as turfPoint,
  booleanPointInPolygon,
  union,
  difference,
  length as turfLength,
  simplify,
} from '@turf/turf';

/** Hard limit before reshape is rejected. */
export const MAX_RESHAPE_VERTICES = 50000;
/** Decimate targets above this before any turf work. */
const PREPARE_VERTEX_THRESHOLD = 280;
/** Max vertices used for reshape math after preparation. */
const COMPUTE_VERTEX_BUDGET = 360;
/** Turf boolean ops budget (outer ring). */
const MAX_BOOLEAN_OUTER_VERTICES = 480;

const TOUCH_TOLERANCE_METERS = 1;
const EPS_LOC = 1e-9;
const EARTH_RADIUS_M = 6371008.8;

export type ReshapeSuccess = {
  coordinates: any;
  action: 'replace' | 'add' | 'remove' | 'trim';
};
export type ReshapeFailure = { reason: string };
export type ReshapeResult = ReshapeSuccess | ReshapeFailure;

export function cloneTargetSnapshot(target: any): any {
  const geo = target.toGeoJSON?.() ?? target;
  return {
    type: geo.type,
    properties: geo.properties ? { ...geo.properties } : {},
    geometry: JSON.parse(JSON.stringify(geo.geometry)),
  };
}

export function countTargetVertices(targetGeojson: any): number {
  const geom = targetGeojson?.geometry;
  if (!geom) return 0;
  if (geom.type === 'LineString') {
    return (geom.coordinates as number[][]).length;
  }
  if (geom.type === 'Polygon') {
    return (geom.coordinates as number[][][]).reduce(
      (sum, ring) => sum + (ring?.length ?? 0),
      0
    );
  }
  return 0;
}

function isClosedRing(coords: number[][]): boolean {
  if (coords.length < 2) return false;
  const first = coords[0];
  const last = coords[coords.length - 1];
  return first[0] === last[0] && first[1] === last[1];
}

/** Fast uniform decimation — much cheaper than repeated turf simplify. */
export function decimateCoords(coords: number[][], maxCount: number): number[][] {
  if (coords.length <= maxCount) return coords;
  const closed = isClosedRing(coords);
  const body = closed ? coords.slice(0, -1) : coords.slice();
  const keep = Math.max(2, maxCount - (closed ? 1 : 0));
  if (body.length <= keep) return coords;

  const step = body.length / keep;
  const out: number[][] = [];
  for (let i = 0; i < keep; i++) {
    out.push(body[Math.min(body.length - 1, Math.floor(i * step))]);
  }
  if (closed && out.length) {
    out.push([out[0][0], out[0][1]]);
  }
  return out;
}

function prepareTargetForReshape(normalized: any): any {
  const geom = normalized?.geometry;
  if (!geom) return normalized;

  if (geom.type === 'LineString') {
    const coords = geom.coordinates as number[][];
    if (coords.length <= PREPARE_VERTEX_THRESHOLD) return normalized;
    return {
      ...normalized,
      geometry: {
        type: 'LineString',
        coordinates: decimateCoords(coords, COMPUTE_VERTEX_BUDGET),
      },
    };
  }

  if (geom.type === 'Polygon') {
    const rings = geom.coordinates as number[][][];
    const outer = rings[0];
    if (!outer || outer.length <= PREPARE_VERTEX_THRESHOLD) return normalized;
    const nextRings = rings.map((ring, index) => {
      const budget = index === 0 ? COMPUTE_VERTEX_BUDGET : Math.min(COMPUTE_VERTEX_BUDGET, ring.length);
      return decimateCoords(ring, budget);
    });
    return {
      ...normalized,
      geometry: { type: 'Polygon', coordinates: nextRings },
    };
  }

  return normalized;
}

function haversineMeters(a: number[], b: number[]): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const dLat = toRad(b[1] - a[1]);
  const dLng = toRad(b[0] - a[0]);
  const lat1 = toRad(a[1]);
  const lat2 = toRad(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

function cumulativeLocationsKm(coords: number[][]): number[] {
  const n = coords.length;
  if (n === 0) return [];
  const locs = new Array<number>(n);
  locs[0] = 0;
  for (let i = 1; i < n; i++) {
    locs[i] = locs[i - 1] + haversineMeters(coords[i - 1], coords[i]) / 1000;
  }
  return locs;
}

export function computeReshape(
  targetGeojson: any,
  drawnCoords: number[][],
  allowSingleIntersection: boolean
): ReshapeResult {
  const normalized = prepareTargetForReshape(
    normalizeReshapeTargetGeoJson(targetGeojson)
  );
  const vertexCount = countTargetVertices(normalized);
  if (vertexCount > MAX_RESHAPE_VERTICES) {
    return { reason: 'tooComplex' };
  }

  const type = normalized?.geometry?.type;
  if (type === 'LineString') {
    const next = reshapeLine(
      normalized.geometry.coordinates as number[][],
      drawnCoords,
      allowSingleIntersection
    );
    return next ?? { reason: 'needTwoCrossings' };
  }
  if (type === 'Polygon') {
    const rings = normalized.geometry.coordinates as number[][][];
    if (!Array.isArray(rings) || rings.length === 0) return { reason: 'invalidSketch' };
    const next = reshapePolygon(rings, drawnCoords);
    return next ?? { reason: 'needTwoCrossings' };
  }
  return { reason: 'unsupportedGeometry' };
}

function normalizeReshapeTargetGeoJson(targetGeoJson: any): any {
  const next = {
    type: targetGeoJson.type,
    properties: targetGeoJson.properties ? { ...targetGeoJson.properties } : {},
    geometry: JSON.parse(JSON.stringify(targetGeoJson.geometry)),
  };
  const geom = next?.geometry;
  if (
    geom?.type === 'MultiPolygon' &&
    Array.isArray(geom.coordinates) &&
    geom.coordinates.length === 1
  ) {
    next.geometry = { type: 'Polygon', coordinates: geom.coordinates[0] };
  } else if (
    geom?.type === 'MultiLineString' &&
    Array.isArray(geom.coordinates) &&
    geom.coordinates.length === 1
  ) {
    next.geometry = { type: 'LineString', coordinates: geom.coordinates[0] };
  }
  return next;
}

function reshapeLine(
  target: number[][],
  drawn: number[][],
  allowSingleIntersection: boolean
): ReshapeSuccess | null {
  const targetLine = turfLineString(target);
  const drawnLine = turfLineString(drawn);
  const crossings = collectCrossings(drawnLine, targetLine);

  if (crossings.length >= 2) {
    const coords = spliceLine(target, drawn, crossings);
    return coords ? { coordinates: coords, action: 'replace' } : null;
  }

  if (crossings.length === 1 && allowSingleIntersection) {
    const coords = trimLineShortestSpur(target, drawn, crossings[0]);
    return coords ? { coordinates: coords, action: 'trim' } : null;
  }

  return null;
}

function reshapePolygon(rings: number[][][], drawn: number[][]): ReshapeSuccess | null {
  const outer = rings[0];
  if (!outer || outer.length < 4) return null;

  const targetPoly = turfPolygon([outer, ...rings.slice(1)]);
  const ringLine = turfLineString(outer);
  const drawnLine = turfLineString(drawn);

  const start = drawn[0];
  const end = drawn[drawn.length - 1];
  const startIn = booleanPointInPolygon(start, targetPoly);
  const endIn = booleanPointInPolygon(end, targetPoly);

  const crossings = collectCrossings(drawnLine, ringLine);

  if (crossings.length >= 2) {
    if (!startIn && !endIn) {
      const removed = removeLensFromPolygon(rings, drawn, crossings);
      if (removed) return { coordinates: removed, action: 'remove' };
    }
    const newOuter = spliceClosedRing(outer, drawn, crossings);
    if (!newOuter) return null;
    return { coordinates: [newOuter, ...rings.slice(1)], action: 'replace' };
  }

  if (startIn && endIn) {
    const added = addSketchToPolygon(rings, drawn);
    if (added) return { coordinates: added, action: 'add' };
    return null;
  }

  return null;
}

type Crossing = { coord: number[]; loc: number };

function collectCrossings(
  sketchLine: ReturnType<typeof turfLineString>,
  targetLine: ReturnType<typeof turfLineString>
): Crossing[] {
  const intersects = lineIntersect(sketchLine as any, targetLine as any);
  const fromIntersect = (intersects?.features ?? []).map((f) => {
    const coord = f.geometry.coordinates as number[];
    const pt = turfPoint(coord);
    return {
      coord,
      loc: nearestPointOnLine(targetLine as any, pt as any).properties?.location ?? 0,
    };
  });

  if (fromIntersect.length >= 2) {
    return fromIntersect.sort((a, b) => a.loc - b.loc);
  }

  const extras: Crossing[] = [];
  for (const coord of [
    sketchLine.geometry.coordinates[0],
    sketchLine.geometry.coordinates[sketchLine.geometry.coordinates.length - 1],
  ]) {
    const nearest = nearestPointOnLine(targetLine as any, turfPoint(coord) as any);
    const distM = (nearest.properties?.dist ?? Infinity) * 1000;
    if (distM <= TOUCH_TOLERANCE_METERS) {
      extras.push({
        coord: nearest.geometry.coordinates as number[],
        loc: nearest.properties?.location ?? 0,
      });
    }
  }

  const merged = [...fromIntersect, ...extras].sort((a, b) => a.loc - b.loc);
  const unique: Crossing[] = [];
  for (const c of merged) {
    if (
      !unique.some(
        (u) => Math.abs(u.loc - c.loc) < EPS_LOC || sqDist(u.coord, c.coord) < 1e-12
      )
    ) {
      unique.push(c);
    }
  }
  return unique;
}

function spliceLine(
  target: number[][],
  drawn: number[][],
  crossings: Crossing[]
): number[][] | null {
  const targetLocs = cumulativeLocationsKm(target);
  const drawnLine = turfLineString(drawn);
  const first = crossings[0];
  const last = crossings[crossings.length - 1];

  const drawnFirstLoc =
    nearestPointOnLine(drawnLine, turfPoint(first.coord)).properties?.location ?? 0;
  const drawnLastLoc =
    nearestPointOnLine(drawnLine, turfPoint(last.coord)).properties?.location ?? 0;
  const orientedDrawn =
    drawnFirstLoc <= drawnLastLoc ? drawn.slice() : drawn.slice().reverse();

  const result: number[][] = [];
  for (let i = 0; i < target.length; i++) {
    if (targetLocs[i] < first.loc - EPS_LOC) result.push(target[i]);
    else break;
  }
  result.push(first.coord);
  for (let i = 1; i < orientedDrawn.length - 1; i++) {
    result.push(orientedDrawn[i]);
  }
  result.push(last.coord);
  for (let i = 0; i < target.length; i++) {
    if (targetLocs[i] > last.loc + EPS_LOC) result.push(target[i]);
  }
  return result.length >= 2 ? result : null;
}

function trimLineShortestSpur(
  target: number[][],
  drawn: number[][],
  crossing: Crossing
): number[][] | null {
  const targetLocs = cumulativeLocationsKm(target);
  const loc = crossing.loc;

  const before: number[][] = [];
  const after: number[][] = [];
  for (let i = 0; i < target.length; i++) {
    const cLoc = targetLocs[i];
    if (cLoc < loc - EPS_LOC) before.push(target[i]);
    else if (cLoc > loc + EPS_LOC) after.push(target[i]);
  }

  const lenBefore = before.length > 0 ? turfLength(turfLineString(before), { units: 'meters' }) : 0;
  const lenAfter = after.length > 0 ? turfLength(turfLineString(after), { units: 'meters' }) : 0;

  const keepBefore = lenBefore >= lenAfter;
  const result = keepBefore ? [...before] : [...after];
  result.push(crossing.coord);

  if (keepBefore) {
    for (let i = 1; i < drawn.length; i++) result.push(drawn[i]);
  } else {
    for (let i = 0; i < drawn.length - 1; i++) result.push(drawn[i]);
  }

  return result.length >= 2 ? result : null;
}

function spliceClosedRing(
  ring: number[][],
  drawn: number[][],
  crossings: Crossing[]
): number[][] | null {
  if (ring.length < 4) return null;

  const ringLocs = cumulativeLocationsKm(ring);
  const drawnLine = turfLineString(drawn);
  const first = crossings[0];
  const last = crossings[crossings.length - 1];

  const drawnMid = drawn[Math.floor(drawn.length / 2)];

  const arcForward: number[][] = [];
  const arcWrap: number[][] = [];
  for (let i = 0; i < ring.length; i++) {
    const loc = ringLocs[i];
    if (loc > first.loc + EPS_LOC && loc < last.loc - EPS_LOC) arcForward.push(ring[i]);
    else if (loc > last.loc + EPS_LOC || loc < first.loc - EPS_LOC) arcWrap.push(ring[i]);
  }

  const arcForwardMid = arcForward[Math.floor(arcForward.length / 2)] ?? first.coord;
  const arcWrapMid = arcWrap[Math.floor(arcWrap.length / 2)] ?? last.coord;
  const replaceForward = sqDist(drawnMid, arcForwardMid) <= sqDist(drawnMid, arcWrapMid);

  const drawnFirstLoc =
    nearestPointOnLine(drawnLine, turfPoint(first.coord)).properties?.location ?? 0;
  const drawnLastLoc =
    nearestPointOnLine(drawnLine, turfPoint(last.coord)).properties?.location ?? 0;
  let orientedDrawn = drawn.slice();
  if (replaceForward) {
    if (drawnFirstLoc > drawnLastLoc) orientedDrawn = orientedDrawn.reverse();
  } else if (drawnLastLoc > drawnFirstLoc) {
    orientedDrawn = orientedDrawn.reverse();
  }

  const result: number[][] = [];
  if (replaceForward) {
    for (let i = 0; i < ring.length; i++) {
      if (ringLocs[i] < first.loc - EPS_LOC) result.push(ring[i]);
    }
    result.push(first.coord);
    for (let i = 1; i < orientedDrawn.length - 1; i++) result.push(orientedDrawn[i]);
    result.push(last.coord);
    for (let i = 0; i < ring.length; i++) {
      if (ringLocs[i] > last.loc + EPS_LOC) result.push(ring[i]);
    }
  } else {
    result.push(first.coord);
    for (let i = 0; i < ring.length; i++) {
      const loc = ringLocs[i];
      if (loc > first.loc + EPS_LOC && loc < last.loc - EPS_LOC) result.push(ring[i]);
    }
    result.push(last.coord);
    for (let i = 1; i < orientedDrawn.length - 1; i++) result.push(orientedDrawn[i]);
  }

  if (result.length < 4) return null;
  const f = result[0];
  const l = result[result.length - 1];
  if (f[0] !== l[0] || f[1] !== l[1]) {
    result.push([f[0], f[1]]);
  }

  try {
    turfPolygon([result]);
  } catch {
    return null;
  }
  return result;
}

function simplifyRingsForBoolean(rings: number[][][]): number[][][] | null {
  const outerLen = rings[0]?.length ?? 0;
  if (outerLen <= MAX_BOOLEAN_OUTER_VERTICES) return rings;

  const decimated = rings.map((ring, index) =>
    decimateCoords(ring, index === 0 ? MAX_BOOLEAN_OUTER_VERTICES : Math.min(MAX_BOOLEAN_OUTER_VERTICES, ring.length))
  );
  if ((decimated[0]?.length ?? 0) <= MAX_BOOLEAN_OUTER_VERTICES) return decimated;

  try {
    const poly = turfPolygon(decimated);
    let tolerance = 0.00001;
    for (let attempt = 0; attempt < 10; attempt++) {
      const simplified = simplify(poly, { tolerance, highQuality: false });
      if (simplified.geometry.type === 'Polygon') {
        const next = simplified.geometry.coordinates as number[][][];
        if ((next[0]?.length ?? 0) <= MAX_BOOLEAN_OUTER_VERTICES) return next;
      }
      tolerance *= 2;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function addSketchToPolygon(rings: number[][][], drawn: number[][]): number[][][] | null {
  const closed = [...drawn];
  const first = closed[0];
  const last = closed[closed.length - 1];
  if (first[0] !== last[0] || first[1] !== last[1]) {
    closed.push([first[0], first[1]]);
  }
  if (closed.length < 4) return null;

  const booleanRings = simplifyRingsForBoolean(rings);
  if (!booleanRings) return null;

  try {
    const targetPoly = turfPolygon(booleanRings);
    const sketchPoly = turfPolygon([closed]);
    const merged = union(targetPoly, sketchPoly);
    if (!merged?.geometry?.coordinates) return null;
    return merged.geometry.coordinates as number[][][];
  } catch {
    return null;
  }
}

function removeLensFromPolygon(
  rings: number[][][],
  drawn: number[][],
  crossings: Crossing[]
): number[][][] | null {
  const outer = rings[0];
  const ringLocs = cumulativeLocationsKm(outer);
  const first = crossings[0];
  const last = crossings[crossings.length - 1];

  const arc: number[][] = [first.coord];
  for (let i = 0; i < outer.length; i++) {
    const loc = ringLocs[i];
    if (loc > first.loc + EPS_LOC && loc < last.loc - EPS_LOC) arc.push(outer[i]);
  }
  arc.push(last.coord);

  const lensRing = [...arc];
  for (let i = drawn.length - 1; i >= 0; i--) {
    lensRing.push(drawn[i]);
  }
  const f = lensRing[0];
  const l = lensRing[lensRing.length - 1];
  if (f[0] !== l[0] || f[1] !== l[1]) {
    lensRing.push([f[0], f[1]]);
  }
  if (lensRing.length < 4) return null;

  const booleanRings = simplifyRingsForBoolean(rings);
  if (!booleanRings) return null;

  try {
    const targetPoly = turfPolygon(booleanRings);
    const lensPoly = turfPolygon([lensRing]);
    const cut = difference(targetPoly, lensPoly);
    if (!cut?.geometry?.coordinates) return null;
    if (cut.geometry.type === 'Polygon') {
      return cut.geometry.coordinates as number[][][];
    }
    if (cut.geometry.type === 'MultiPolygon') {
      const parts = cut.geometry.coordinates as number[][][][];
      return pickLargestPolygonPart(parts);
    }
  } catch {
    return null;
  }
  return null;
}

function pickLargestPolygonPart(parts: number[][][][]): number[][][] | null {
  let best: number[][][] | null = null;
  let bestLen = -1;
  for (const part of parts) {
    try {
      const ring = part[0];
      if (!ring) continue;
      const len = turfLength(turfLineString(ring), { units: 'meters' });
      if (len > bestLen) {
        bestLen = len;
        best = part;
      }
    } catch {
      /* ignore */
    }
  }
  return best;
}

function sqDist(a: number[], b: number[]): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
}
